<template>
  <v-select
      v-model="internalValue"
      :items="items"
      item-title="title"
      item-value="value"
      :label="label"
      clearable
      :hide-details="hideDetails"
      :density="density"
      :variant="useGlobalSetting().globalTextVariant"
      :readonly="readonly"
      :class="readonly ? 'custom-readonly' : ''"
      :disabled="disabled"
      :prepend-inner-icon-icon="prependIcon"
  >
    <template v-slot:item="{ props: itemProps, item }">
      <v-list-item v-bind="itemProps" :subtitle="item.raw.subtitle">
        <template v-slot:prepend>
          <span class="area-dot mr-2" :style="{ backgroundColor: item.raw.color }"/>
        </template>
      </v-list-item>
    </template>
    <template v-slot:selection="{ item }">
      <span class="d-flex align-center ga-2">
        <span class="area-dot" :style="{ backgroundColor: item.raw.color }"/>
        <span>{{ item.raw.title }}</span>
      </span>
    </template>
  </v-select>
</template>

<script lang="ts" setup>
import {computed, onMounted} from "vue";
import {useLotoAreaStore} from "@/api/loto";
import {planFullName} from "@/types/Loto";
import {useGlobalSetting} from "@/lib/utils";

/**
 * Auswahl eines Freischaltbereichs.
 *
 * Zwei Modi:
 * - filterMode=true  → Model ist ein Filter-String "=<id>" (für UniversalList-Spaltenfilter)
 * - filterMode=false → Model ist die loto_area_id (number | null) direkt (für Formulare)
 */

interface Props {
  filterMode?: boolean,
  label?: string,
  readonly?: boolean,
  disabled?: boolean,
  hideDetails?: boolean,
  density?: 'default' | 'comfortable' | 'compact',
  prependIcon?: string,
}

const props = withDefaults(defineProps<Props>(), {
  filterMode: false,
  label: "Bereich",
  readonly: false,
  disabled: false,
  hideDetails: true,
  density: 'compact',
  prependIcon: "",
})

const modelValue = defineModel<string | number | null>();

const areaStore = useLotoAreaStore();

onMounted(() => {
  areaStore.load();
});

const items = computed(() => {
  // Nach Plan-Reihenfolge (Maschine → Ebene) gruppieren, innerhalb des Plans alphabetisch
  const planOrder = (planId: number) => {
    const index = areaStore.plans.findIndex(p => p.id === planId)
    return index === -1 ? Number.MAX_SAFE_INTEGER : index
  }
  const sorted = [...areaStore.areas].sort((a, b) =>
      planOrder(a.plan_id) - planOrder(b.plan_id) || a.name.localeCompare(b.name, 'de'))
  return sorted.map(area => ({
    title: area.name,
    value: area.id,
    color: area.color,
    subtitle: planFullName(areaStore.planById(area.plan_id)),
  }))
})

const internalValue = computed<number | null>({
  get() {
    if (!props.filterMode) return (modelValue.value as number | null) ?? null
    const raw = String(modelValue.value ?? '')
    const match = raw.match(/^=?(\d+)$/)
    return match ? Number(match[1]) : null
  },
  set(val: number | null) {
    if (!props.filterMode) {
      modelValue.value = val
    } else {
      modelValue.value = val != null ? `=${val}` : ''
    }
  }
})
</script>

<style scoped>
.area-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.3);
}
</style>
